/**
 * Boot the shared oraChat panel's add-in transport (outlook-orachat-uplift s5).
 *
 * `setOraChatTransport` / `setOraChatAvatarSrc` are module-level in the panel
 * (`app/features/oraChatV2/transport.ts`), so the install must happen ONCE, at
 * add-in boot, BEFORE the first render (`main.tsx`). An install in an effect
 * would run after the panel's own mount effects: its first requests would go
 * out bare to the add-in origin, whose SPA fallback answers index.html 200.
 *
 * The chat's token is SILENT only (`getProductApiTokenSilent`, or the proof
 * harness's): a turn never opens a sign-in dialog. When it throws
 * `SignInRequiredError` the transport marks the sign-in store, and the
 * Assistant pane shows its "Your sign-in has expired" card, whose "Sign in"
 * button runs the add-in's existing interactive sign-in. Whenever the silent
 * getter succeeds again (the token came back by any route: the card, Needs
 * you's dialog, another realm's stored token), the store clears, so a later
 * unrelated failure shows the panel's own error, not the sign-in card.
 *
 * No MSAL here (design C15).
 */

import { useSyncExternalStore } from "react";

import {
  setOraChatSessionStore,
  setOraChatVoiceHost,
} from "@product-app/features/oraChatV2/transport";

import { installAddinOraChatTransport } from "./addinOraChatTransport";
import { addinVoiceHost } from "./addinVoiceHost";
import { paneMemoryStore } from "./paneMemoryStore";

type Listener = () => void;

let signInRequired = false;
const listeners = new Set<Listener>();

function setSignInRequired(next: boolean): void {
  if (signInRequired === next) return;
  signInRequired = next;
  listeners.forEach((listener) => listener());
}

/** Whether the chat needs an interactive sign-in before its next request. */
export const chatSignIn = {
  isRequired: (): boolean => signInRequired,
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** The silent token is gone: show the sign-in card. */
  markRequired: (): void => setSignInRequired(true),
  /** An interactive sign-in succeeded: the chat can send again. */
  markSignedIn: (): void => setSignInRequired(false),
};

/** `chatSignIn.isRequired()` as React state. */
export function useChatSignInRequired(): boolean {
  return useSyncExternalStore(chatSignIn.subscribe, chatSignIn.isRequired, chatSignIn.isRequired);
}

let tokenGetter: (() => Promise<string>) | null = null;

/** The chat's (silent) Product API token — the getter `bootOraChat` installed. */
export function getChatToken(): Promise<string> {
  if (!tokenGetter) return Promise.reject(new Error("oraChat transport is not booted"));
  return tokenGetter();
}

export interface BootOraChatOptions {
  /** Silent token getter: throws `SignInRequiredError`, never opens a dialog. */
  getToken: () => Promise<string>;
  /** Default: `getApiBaseUrl()` at call time ("" = same-origin through the host relay). */
  apiBase?: string;
}

/** Install the add-in transport + avatar into the panel. Returns the uninstall. */
export function bootOraChat({ getToken, apiBase }: BootOraChatOptions): () => void {
  // A good silent token means sign-in is no longer required, however it came back.
  const silentToken = async (): Promise<string> => {
    const token = await getToken();
    setSignInRequired(false);
    return token;
  };
  tokenGetter = silentToken;
  const uninstall = installAddinOraChatTransport({
    getToken: silentToken,
    apiBase,
    onSignInRequired: chatSignIn.markRequired,
  });
  setOraChatVoiceHost(addinVoiceHost);
  setOraChatSessionStore(paneMemoryStore);
  return () => {
    uninstall();
    setOraChatVoiceHost(null);
    setOraChatSessionStore(null);
    paneMemoryStore.clear();
    tokenGetter = null;
    setSignInRequired(false);
  };
}
